/**
 * ============================================================================
 * CREATE TASK MODAL v4.2 - TIME FIELD FOR CALENDAR
 * ============================================================================
 *
 * ✅ v4.2: TIME FIELD
 *    - Uhrzeit-Eingabefeld im Formular
 *    - Standard-Uhrzeit: 18:00
 *    - Wird an Kalendereintrag übergeben
 *
 * ✅ v4.1: CALENDAR BUTTON
 *    - Button "📅 Kalender" im Edit-Modus
 *    - Erstellt direkt Kalendereintrag (ohne Bestätigung)
 *
 * ✅ v4.0: MENTAL LOAD BADGE SUPPORT
 *    - Setzt isMentalLoad: true für Mental Load Tasks
 *    - Tasks werden in TasksOverview mit 🧠 Badge markiert
 *
 * ✅ v3.9: MENTAL LOAD WITH SUBTASKS
 *    - Mental Load Picker befüllt jetzt auch Beschreibung + Subtasks
 *    - 50 Aufgaben mit je 4-6 vordefinierten Subtasks
 *
 * ✅ v3.8: MENTAL LOAD PICKER
 * ✅ v3.7: DIRECT HOUSEHOLD CONTEXT
 * ✅ v3.6: INTEGER ID SUPPORT
 * ✅ v3.5: PLANIVIO REBRANDING
 *
 * @version 4.2.0 - Time Field for Calendar
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHousehold } from '../../context/HouseholdContext';
import MentalLoadPicker from '../Notes/MentalLoadPicker';

console.log('✅ CreateTaskModal v4.2 loaded - MIT UHRZEIT-FELD 🕐');

// ============================================================================
// CONSTANTS
// ============================================================================
const TASK_CATEGORIES = [
  { id: 'personal', name: '🏠 Persönlich' },
  { id: 'work', name: '💼 Arbeit' },
  { id: 'household', name: '🏡 Haushalt' },
  { id: 'shopping', name: '🛒 Einkauf' },
  { id: 'ideas', name: '💡 Ideen' }
];

const TASK_PRIORITIES = [
  { id: 'low', name: '🟢 Niedrig' },
  { id: 'medium', name: '🟡 Mittel' },
  { id: 'high', name: '🔴 Hoch' }
];

// ✅ v4.2: Aktuelle Uhrzeit als Default
const getCurrentTime = () => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const DEFAULT_FORM_DATA = {
  title: '',
  description: '',
  category: 'personal',
  priority: 'medium',
  due_date: '',
  time: '',  // ✅ v4.2: Wird dynamisch auf aktuelle Zeit gesetzt
  person: 'alle'
};

// Fallback wenn keine memberOptions übergeben werden
const DEFAULT_MEMBERS = [
  { id: 'alle', value: 'alle', name: 'Alle', avatar: '👥' }
];

// ============================================================================
// ✅ v3.5: PLANIVIO COLORS
// ============================================================================
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#FFFFFF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

// ============================================================================
// ✅ v3.8: PLANIVIO COMPACT STYLES
// ============================================================================
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modal: {
    background: '#FFFFFF',
    borderRadius: '16px',
    border: '2px solid #B8D4DC',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(74, 144, 164, 0.15)'
  },
  // ✅ COMPACT HEADER
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #B8D4DC',
    background: '#f3f4f6'
  },
  headerTitle: {
    margin: 0,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  closeBtn: {
    width: '24px',
    height: '24px',
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    borderRadius: '6px',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    padding: 0,
    lineHeight: 1
  },
  // ✅ COMPACT FORM
  form: {
    padding: '12px'
  },
  formGroup: {
    marginBottom: '10px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem'
  },
  input: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.85rem',
    background: 'white',
    color: '#1F2937',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.85rem',
    background: 'white',
    color: '#1F2937',
    resize: 'vertical',
    fontFamily: 'inherit',
    minHeight: '60px',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.8rem',
    background: 'white',
    color: '#1F2937',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    outline: 'none'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  // ✅ COMPACT SUBTASKS
  subtasksSection: {
    marginBottom: '10px',
    padding: '10px',
    background: '#E8F4F8',
    borderRadius: '8px',
    border: '1.5px solid #B8D4DC'
  },
  subtasksHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  subtasksTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  subtasksCount: {
    fontSize: '0.65rem',
    color: '#6B7280',
    background: 'white',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  subtasksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '8px'
  },
  subtaskItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    background: 'white',
    borderRadius: '6px',
    border: '1px solid #D4E8EC'
  },
  subtaskCheckbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: '#4A90A4'
  },
  subtaskInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '0.8rem',
    color: '#1F2937',
    outline: 'none'
  },
  subtaskInputDone: {
    textDecoration: 'line-through',
    color: '#9CA3AF'
  },
  subtaskDeleteBtn: {
    width: '20px',
    height: '20px',
    border: 'none',
    background: '#FEE2E2',
    color: '#EF4444',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    transition: 'all 0.2s'
  },
  addSubtaskBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'white',
    border: '1.5px dashed #B8D4DC',
    borderRadius: '6px',
    color: '#4A90A4',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },
  // ✅ COMPACT FOOTER
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px solid #D4E8EC'
  },
  btnCancel: {
    padding: '6px 12px',
    background: 'white',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnSave: {
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
    transition: 'all 0.2s'
  },
  btnDelete: {
    padding: '6px 12px',
    background: '#FEF2F2',
    color: '#EF4444',
    border: '1px solid #FECACA',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s'
  },
  // ✅ v4.1: Kalender-Button
  btnCalendar: {
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
    transition: 'all 0.2s'
  },
  spacer: {
    flex: 1
  },
  // Debug Badge - COMPACT
  debugBadge: {
    fontSize: '0.65rem',
    background: '#D4E8EC',
    color: '#4A90A4',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '6px'
  },
  // ✅ v3.8: Mental Load Button
  mentalLoadBtn: {
    padding: '4px 10px',
    height: '28px',
    border: 'none',
    background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    transition: 'all 0.2s',
    marginLeft: 'auto',
    marginRight: '8px',
    boxShadow: '0 2px 6px rgba(236, 72, 153, 0.3)',
    whiteSpace: 'nowrap'
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
const CreateTaskModal = ({
  show,
  onClose,
  editingTask,
  onSave,
  onDelete,
  onCreateCalendarEntry,  // ✅ v4.1: Kalender-Funktion
  memberOptions: memberOptionsProp = null  // ✅ v3.7: Optional, wird nicht mehr genutzt
}) => {
  
  // ============================================
  // ✅ v3.7: DIREKT VOM HOUSEHOLD CONTEXT
  // ============================================
  const { members: householdMembers, getMemberOptions, loading: membersLoading } = useHousehold();
  
  // ============================================
  // STATE
  // ============================================
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [subtasks, setSubtasks] = useState([]);
  const [showMentalLoadPicker, setShowMentalLoadPicker] = useState(false);
  const [isMentalLoad, setIsMentalLoad] = useState(false); // ✅ v4.0: Mental Load Flag

  // ============================================
  // ✅ v3.7: MEMOIZED Members DIREKT vom Context
  // ============================================
  const FAMILY_MEMBERS = useMemo(() => {
    // Hole Options direkt vom Context
    const options = getMemberOptions ? getMemberOptions(true) : [];
    
    console.log('🔍 CreateTaskModal FAMILY_MEMBERS from Context:', options?.length);
    
    // Wenn leer, verwende Default
    if (!options || options.length === 0) {
      console.warn('⚠️ No members from context, using DEFAULT_MEMBERS');
      return DEFAULT_MEMBERS;
    }
    
    // ✅ v3.7: Konvertiere values zu Strings für SELECT
    return options.map(opt => ({
      ...opt,
      value: String(opt.value)  // SELECT braucht String values
    }));
  }, [getMemberOptions, householdMembers]); // Re-run wenn Members sich ändern

  const completedSubtasks = useMemo(() => 
    subtasks.filter(s => s.done === 1).length, 
    [subtasks]
  );

  // ============================================
  // useEffect: Form initialisieren
  // ============================================
  useEffect(() => {
    if (!show) return;
    
    console.log('✅ Modal opened, editingTask:', editingTask?.id);
    console.log('👥 Available members:', FAMILY_MEMBERS.length);
    console.log('👥 Members loading:', membersLoading);
    
    if (editingTask) {
      // ✅ v3.6: assigned_to kann INTEGER sein, konvertiere zu String für SELECT
      const rawPerson = editingTask.person || editingTask.assignedTo || editingTask.assigned_to;
      const personValue = (!rawPerson || rawPerson === 'alle') ? 'alle' : String(rawPerson);

      console.log('👤 Person value resolved:', personValue, '(raw:', rawPerson, ')');

      // ✅ v4.3: Debug - Alle möglichen Datumsfelder prüfen
      console.log('📅 Task date fields:', {
        due_date: editingTask.due_date,
        dueDate: editingTask.dueDate,
        deadline: editingTask.deadline,
        date: editingTask.date,
        allKeys: Object.keys(editingTask)
      });

      // ✅ v4.3: Datum aus verschiedenen möglichen Feldern extrahieren
      let rawDate = editingTask.due_date || editingTask.dueDate || editingTask.deadline || editingTask.date || '';
      // Falls ISO-String, nur Datumsteil verwenden
      if (rawDate && rawDate.includes('T')) {
        rawDate = rawDate.split('T')[0];
      }

      setFormData({
        title: editingTask.title || '',
        description: editingTask.description || '',
        category: editingTask.category || 'personal',
        priority: editingTask.priority || 'medium',
        due_date: rawDate,
        time: editingTask.time || getCurrentTime(),  // ✅ v4.2: Uhrzeit laden oder aktuelle Zeit
        person: personValue
      });
      setSubtasks(editingTask.subtasks || []);
      setIsMentalLoad(editingTask.isMentalLoad || false); // ✅ v4.0
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'personal',
        priority: 'medium',
        due_date: '',
        time: getCurrentTime(),  // ✅ v4.2: Aktuelle Uhrzeit
        person: 'alle'
      });
      setSubtasks([]);
      setIsMentalLoad(false); // ✅ v4.0
    }
  }, [show, editingTask, FAMILY_MEMBERS.length, membersLoading]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('📝 Input change:', name, '=', value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddSubtask = () => {
    setSubtasks(prev => [...prev, {
      id: 'new-' + Date.now(),
      title: '',
      done: 0
    }]);
  };

  const handleSubtaskChange = (index, value) => {
    setSubtasks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], title: value };
      return updated;
    });
  };

  const handleSubtaskToggle = (index) => {
    setSubtasks(prev => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        done: updated[index].done === 1 ? 0 : 1 
      };
      return updated;
    });
  };

  const handleDeleteSubtask = (index) => {
    setSubtasks(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Bitte gib einen Titel ein');
      return;
    }
    
    const validSubtasks = subtasks.filter(s => s.title && s.title.trim());
    
    const taskData = {
      title: formData.title,
      description: formData.description,
      category: formData.category,
      priority: formData.priority,
      due_date: formData.due_date,
      dueDate: formData.due_date,
      person: formData.person,
      assignedTo: formData.person,
      assigned_to: formData.person,
      done: editingTask?.done || 0,
      subtasks: validSubtasks,
      isMentalLoad: isMentalLoad // ✅ v4.0: Mental Load Badge Flag
    };
    
    console.log('✅ Submitting task:', taskData.title);
    console.log('👤 assigned_to:', formData.person);
    
    onSave(taskData);
  };

  const handleDelete = () => {
    if (editingTask && window.confirm('Aufgabe wirklich löschen?')) {
      onDelete(editingTask.id);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // ============================================
  // ✅ v3.8: MENTAL LOAD PICKER HANDLERS
  // ============================================
  const handleOpenMentalLoadPicker = useCallback(() => {
    console.log('🧠 Opening Mental Load Picker in Task Modal');
    setShowMentalLoadPicker(true);
  }, []);

  const handleCloseMentalLoadPicker = useCallback(() => {
    setShowMentalLoadPicker(false);
  }, []);

  const handleSelectMentalLoadTask = useCallback((tasks) => {
    if (tasks.length > 0) {
      const selectedTask = tasks[0]; // Nur erste Aufgabe übernehmen
      console.log('🧠 Selected Mental Load task:', selectedTask.name);
      console.log('🧠 Description:', selectedTask.description);
      console.log('🧠 Subtasks:', selectedTask.subtasks?.length || 0);

      // Titel, Beschreibung und Kategorie befüllen
      setFormData(prev => ({
        ...prev,
        title: `${selectedTask.emoji} ${selectedTask.name}`,
        description: selectedTask.description || '',
        category: 'household' // Mental Load = Haushalt Kategorie
      }));

      // ✅ v4.0: Mental Load Flag setzen für Badge in TasksOverview
      setIsMentalLoad(true);
      console.log('🧠 Mental Load Flag set to TRUE');

      // Subtasks übernehmen (mit eindeutigen IDs)
      if (selectedTask.subtasks && selectedTask.subtasks.length > 0) {
        const newSubtasks = selectedTask.subtasks.map((st, index) => ({
          id: `ml-subtask-${Date.now()}-${index}`,
          title: st.title,
          done: st.done || 0
        }));
        setSubtasks(newSubtasks);
        console.log('🧠 Subtasks loaded:', newSubtasks.length);
      }
    }
    setShowMentalLoadPicker(false);
  }, []);

  // ============================================
  // EARLY RETURN
  // ============================================
  if (!show) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            {editingTask ? '✏️ Aufgabe bearbeiten' : '➕ Neue Aufgabe'}
            <span style={styles.debugBadge}>
              👥 {FAMILY_MEMBERS.length}
            </span>
          </h2>

          {/* ✅ v3.8: Mental Load Picker Button */}
          <button
            type="button"
            style={styles.mentalLoadBtn}
            onClick={handleOpenMentalLoadPicker}
            title="Mental Load Checkliste"
          >
            🧠 Mental Load
          </button>

          <button
            style={styles.closeBtn}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          
          {/* Title */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Aufgabe *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Was muss erledigt werden?"
              required
              autoFocus
              style={styles.input}
            />
          </div>

          {/* Description */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optionale Details..."
              rows="3"
              style={styles.textarea}
            />
          </div>

          {/* Subtasks Section */}
          <div style={styles.subtasksSection}>
            <div style={styles.subtasksHeader}>
              <span style={styles.subtasksTitle}>
                📋 Unteraufgaben
              </span>
              {subtasks.length > 0 && (
                <span style={styles.subtasksCount}>
                  {completedSubtasks}/{subtasks.length} erledigt
                </span>
              )}
            </div>

            {subtasks.length > 0 && (
              <div style={styles.subtasksList}>
                {subtasks.map((subtask, index) => (
                  <div key={subtask.id || index} style={styles.subtaskItem}>
                    <input
                      type="checkbox"
                      checked={subtask.done === 1}
                      onChange={() => handleSubtaskToggle(index)}
                      style={styles.subtaskCheckbox}
                    />
                    <input
                      type="text"
                      value={subtask.title}
                      onChange={(e) => handleSubtaskChange(index, e.target.value)}
                      placeholder="Unteraufgabe..."
                      style={{
                        ...styles.subtaskInput,
                        ...(subtask.done === 1 ? styles.subtaskInputDone : {})
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteSubtask(index)}
                      style={styles.subtaskDeleteBtn}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddSubtask}
              style={styles.addSubtaskBtn}
            >
              ➕ Unteraufgabe hinzufügen
            </button>
          </div>

          {/* Category & Priority */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Kategorie</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={styles.select}
              >
                {TASK_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Priorität</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                style={styles.select}
              >
                {TASK_PRIORITIES.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Due Date, Time & Person */}
          <div style={{...styles.formRow, gridTemplateColumns: '1fr 0.7fr 1fr'}}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Fällig am</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            {/* ✅ v4.2: Uhrzeit für Kalendertermin */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Uhrzeit</label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            {/* ✅ v3.7: Zuständig mit Loading-Anzeige */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Zuständig
                <span style={{...styles.debugBadge, marginLeft: '0.5rem'}}>
                  {membersLoading ? '⏳' : `${FAMILY_MEMBERS.length} Optionen`}
                </span>
              </label>
              <select
                name="person"
                value={formData.person}
                onChange={handleChange}
                style={styles.select}
                disabled={membersLoading}
              >
                {FAMILY_MEMBERS.map(member => (
                  <option key={member.value || member.id} value={member.value}>
                    {member.avatar} {member.name}
                    {member.isMe ? ' (Ich)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div style={styles.actions}>
            {editingTask && (
              <button
                type="button"
                style={styles.btnDelete}
                onClick={handleDelete}
              >
                🗑️ Löschen
              </button>
            )}

            {/* ✅ v4.1: Kalender-Button - für Edit UND neue Aufgaben */}
            {onCreateCalendarEntry && formData.title && (
              <button
                type="button"
                style={styles.btnCalendar}
                onClick={() => {
                  // Verwende aktuelle Formulardaten (nicht editingTask)
                  const taskForCalendar = {
                    title: formData.title,
                    description: formData.description,
                    due_date: formData.due_date,
                    time: formData.time,  // ✅ v4.2: Uhrzeit übergeben
                    person: formData.person
                  };
                  console.log('📅 Creating calendar entry for task:', taskForCalendar.title, 'at', taskForCalendar.time);
                  onCreateCalendarEntry(taskForCalendar);
                }}
              >
                📅 Kalender
              </button>
            )}

            <div style={styles.spacer}></div>

            <button
              type="button"
              style={styles.btnCancel}
              onClick={onClose}
            >
              Abbrechen
            </button>

            <button
              type="submit"
              style={styles.btnSave}
            >
              💾 Speichern
            </button>
          </div>
        </form>
      </div>

      {/* ✅ v3.8: Mental Load Picker Modal */}
      <MentalLoadPicker
        isOpen={showMentalLoadPicker}
        onClose={handleCloseMentalLoadPicker}
        onAddTasks={handleSelectMentalLoadTask}
      />
    </div>
  );
};

export default CreateTaskModal;